/**
 * 타로 78장 데이터.
 * - 메이저 아르카나 22장: 카드별 키워드 직접 정의
 * - 마이너 아르카나 56장: 슈트 테마 × 랭크 의미 조합으로 생성 (TODO: 카드별로 다듬기)
 * id 0~21 = 메이저, 22~77 = 마이너(wands→cups→swords→pentacles, Ace→King)
 */

export type Arcana = 'major' | 'minor';
export type Suit = 'wands' | 'cups' | 'swords' | 'pentacles';
export type Rank =
  | 'ace'
  | 'two'
  | 'three'
  | 'four'
  | 'five'
  | 'six'
  | 'seven'
  | 'eight'
  | 'nine'
  | 'ten'
  | 'page'
  | 'knight'
  | 'queen'
  | 'king';

export interface Card {
  id: number;
  arcana: Arcana;
  suit?: Suit;
  rank?: Rank;
  nameKo: string;
  nameEn: string;
  keywordsUp: string[];
  keywordsRev: string[];
  /** 카드 이미지 파일 키 (예: cards/00-the-fool.webp) — apps/web/public/cards 에서 서빙 */
  imageKey: string;
}

/** 뽑힌 카드 1장 */
export interface DrawnCard {
  cardId: number;
  reversed: boolean;
  position: number;
}

const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

// ── 메이저 아르카나 ────────────────────────────────────────────────
type MajorDef = [nameEn: string, nameKo: string, up: string[], rev: string[]];
const MAJOR: MajorDef[] = [
  ['The Fool', '바보', ['새로운 시작', '순수함', '모험'], ['무모함', '경솔한 결정', '방향 상실']],
  ['The Magician', '마법사', ['의지', '창조력', '실행'], ['속임수', '재능 낭비', '자신감 부족']],
  [
    'The High Priestess',
    '여사제',
    ['직관', '비밀', '내면의 지혜'],
    ['감정 억압', '표면적 판단', '단절'],
  ],
  ['The Empress', '여황제', ['풍요', '양육', '감각적 만족'], ['의존', '과잉보호', '창조력 정체']],
  ['The Emperor', '황제', ['안정', '권위', '구조'], ['독선', '경직', '통제 상실']],
  ['The Hierophant', '교황', ['전통', '가르침', '소속'], ['형식주의', '반항', '가치관 충돌']],
  ['The Lovers', '연인', ['사랑', '선택', '조화'], ['불화', '유혹', '잘못된 선택']],
  ['The Chariot', '전차', ['승리', '추진력', '자기 통제'], ['방향 상실', '조급함', '통제 불능']],
  ['Strength', '힘', ['용기', '인내', '부드러운 힘'], ['나약함', '자기 의심', '충동']],
  ['The Hermit', '은둔자', ['성찰', '고독', '내면 탐구'], ['고립', '외로움', '현실 회피']],
  [
    'Wheel of Fortune',
    '운명의 수레바퀴',
    ['전환점', '행운', '순환'],
    ['불운', '저항', '예상 밖 변화'],
  ],
  ['Justice', '정의', ['공정', '균형', '진실'], ['불공정', '책임 회피', '편향']],
  ['The Hanged Man', '매달린 사람', ['관점 전환', '희생', '기다림'], ['정체', '헛된 희생', '미련']],
  ['Death', '죽음', ['끝과 시작', '변화', '놓아줌'], ['변화 거부', '집착', '정체']],
  ['Temperance', '절제', ['균형', '조화', '치유'], ['불균형', '과잉', '조급함']],
  ['The Devil', '악마', ['집착', '유혹', '속박'], ['해방', '자각', '관계 정리']],
  ['The Tower', '탑', ['급변', '붕괴', '깨달음'], ['재난 회피', '변화 지연', '두려움']],
  ['The Star', '별', ['희망', '영감', '회복'], ['실망', '자신감 상실', '방향 상실']],
  ['The Moon', '달', ['불안', '환상', '무의식'], ['혼란 해소', '진실 드러남', '두려움 극복']],
  ['The Sun', '태양', ['성공', '기쁨', '활력'], ['일시적 우울', '과신', '지연된 성공']],
  ['Judgement', '심판', ['각성', '재평가', '부름'], ['자기 비판', '후회', '결정 회피']],
  ['The World', '세계', ['완성', '성취', '통합'], ['미완성', '지연', '마무리 부족']],
];

// ── 마이너 아르카나 ────────────────────────────────────────────────
const SUITS: Record<Suit, { ko: string; theme: string; themeRev: string }> = {
  wands: { ko: '완드', theme: '열정·행동', themeRev: '소진·조급함' },
  cups: { ko: '컵', theme: '감정·관계', themeRev: '감정 불안·상실' },
  swords: { ko: '소드', theme: '사고·결단', themeRev: '갈등·혼란' },
  pentacles: { ko: '펜타클', theme: '물질·현실', themeRev: '재정 불안·정체' },
};

const RANKS: Record<Rank, { ko: string; en: string; up: string[]; rev: string[] }> = {
  ace: { ko: '에이스', en: 'Ace', up: ['새 기회', '잠재력'], rev: ['기회 놓침', '지연'] },
  two: { ko: '2', en: 'Two', up: ['선택', '균형'], rev: ['우유부단', '불균형'] },
  three: { ko: '3', en: 'Three', up: ['확장', '협력'], rev: ['지연', '불협화음'] },
  four: { ko: '4', en: 'Four', up: ['안정', '휴식'], rev: ['정체', '불안정'] },
  five: { ko: '5', en: 'Five', up: ['갈등', '시련'], rev: ['회복', '화해'] },
  six: { ko: '6', en: 'Six', up: ['성취', '나눔'], rev: ['자만', '불공정'] },
  seven: { ko: '7', en: 'Seven', up: ['평가', '인내'], rev: ['조급함', '자기 회의'] },
  eight: { ko: '8', en: 'Eight', up: ['진전', '숙련'], rev: ['지연', '완벽주의'] },
  nine: { ko: '9', en: 'Nine', up: ['완성 임박', '만족'], rev: ['불안', '과욕'] },
  ten: { ko: '10', en: 'Ten', up: ['완결', '충만'], rev: ['부담', '붕괴'] },
  page: { ko: '시종', en: 'Page', up: ['소식', '호기심'], rev: ['미숙함', '나쁜 소식'] },
  knight: { ko: '기사', en: 'Knight', up: ['추진', '움직임'], rev: ['성급함', '정체'] },
  queen: { ko: '여왕', en: 'Queen', up: ['성숙', '보살핌'], rev: ['감정 과잉', '냉담'] },
  king: { ko: '왕', en: 'King', up: ['통제', '리더십'], rev: ['독선', '무책임'] },
};

const SUIT_ORDER: Suit[] = ['wands', 'cups', 'swords', 'pentacles'];
const RANK_ORDER: Rank[] = [
  'ace',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'page',
  'knight',
  'queen',
  'king',
];

function buildCards(): Card[] {
  const majors: Card[] = MAJOR.map(([nameEn, nameKo, keywordsUp, keywordsRev], i) => ({
    id: i,
    arcana: 'major',
    nameKo,
    nameEn,
    keywordsUp,
    keywordsRev,
    imageKey: `cards/${String(i).padStart(2, '0')}-${slug(nameEn)}.webp`,
  }));

  const minors: Card[] = [];
  let id = 22;
  for (const suit of SUIT_ORDER) {
    const s = SUITS[suit];
    for (const rank of RANK_ORDER) {
      const r = RANKS[rank];
      const nameEn = `${r.en} of ${suit[0]!.toUpperCase()}${suit.slice(1)}`;
      minors.push({
        id,
        arcana: 'minor',
        suit,
        rank,
        nameKo: `${s.ko} ${r.ko}`,
        nameEn,
        keywordsUp: [...r.up, s.theme],
        keywordsRev: [...r.rev, s.themeRev],
        imageKey: `cards/${String(id).padStart(2, '0')}-${slug(nameEn)}.webp`,
      });
      id++;
    }
  }
  return [...majors, ...minors];
}

export const CARDS: readonly Card[] = Object.freeze(buildCards());
export const CARD_COUNT = 78;

export function getCard(id: number): Card {
  const c = CARDS[id];
  if (!c) throw new RangeError(`invalid card id: ${id}`);
  return c;
}

/** 카드 1장을 사람이 읽는 문자열로 (LLM 프롬프트/UI용) */
export function describeCard(d: DrawnCard): string {
  const c = getCard(d.cardId);
  const dir = d.reversed ? '역방향' : '정방향';
  const kw = (d.reversed ? c.keywordsRev : c.keywordsUp).join(', ');
  return `${c.nameKo}(${c.nameEn}) ${dir} — ${kw}`;
}

/* ── 카드 도감(SEO) 페이지용 ─────────────────────────────────────── */

/** 카드 상세 페이지 URL 슬러그 — `/cards/the-fool` (이미지 키와 같은 규칙) */
export function cardSlug(c: Card): string {
  return slug(c.nameEn);
}

/** 슬러그로 카드 찾기 (없으면 undefined) */
export function findCardBySlug(s: string): Card | undefined {
  const key = slug(s);
  return CARDS.find((c) => cardSlug(c) === key);
}

/** 아르카나·슈트 한글 표기 (도감 분류·본문용) */
export const ARCANA_LABELS: Record<Arcana, string> = {
  major: '메이저 아르카나',
  minor: '마이너 아르카나',
};

export const SUIT_LABELS: Record<Suit, { ko: string; theme: string }> = {
  wands: { ko: '완드', theme: '열정과 행동' },
  cups: { ko: '컵', theme: '감정과 관계' },
  swords: { ko: '소드', theme: '사고와 결단' },
  pentacles: { ko: '펜타클', theme: '물질과 현실' },
};
